/**
 * The referral page's three reads and two writes (COL-528).
 *
 * The owner's OWN code and the claim they arrived with already come from
 * `my_owner_referral` (useOwnerReferral, PR #1728). This adds the outbound
 * side: who used their code, redeeming one from inside the portal, and the
 * email invitation.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, supabaseUrl } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

/** The four states the page shows. Mirrors `my_owner_referrals().status`. */
export type OwnerReferralStage =
  | "enquired"
  | "onboarding"
  | "contract_signed"
  | "credited";

export type OwnerReferralRow = {
  referred_name: string | null;
  referred_email: string | null;
  code_used: string;
  entered_at: string;
  status: OwnerReferralStage;
  /** The month a credit landed on; null until billing grants one. */
  reward_month: string | null;
};

/**
 * Keyed by account, as accountBillingAddressKey already is. Neither sign-out
 * nor impersonation clears the QueryClient, so an unscoped key hands the next
 * account the previous owner's rows while its own request is in flight — and
 * impersonation is exactly how this portal is meant to be reviewed first.
 */
export function myOwnerReferralsKey(userId: string | undefined) {
  return ["owner", "my-referrals", userId] as const;
}

function isMissingFunction(error: {
  code?: string;
  message?: string;
}): boolean {
  if (error?.code === "PGRST202" || error?.code === "42883") return true;
  return /could not find the function|does not exist/i.test(
    error?.message ?? ""
  );
}

export function useMyOwnerReferrals(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: myOwnerReferralsKey(user?.id),
    enabled: enabled && !!user?.id,
    queryFn: async (): Promise<OwnerReferralRow[]> => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (fn: string) => Promise<{
            data: unknown;
            error: { code?: string; message?: string } | null;
          }>;
        }
      ).rpc("my_owner_referrals");
      if (error) {
        if (isMissingFunction(error)) return [];
        throw error;
      }
      return (data ?? []) as OwnerReferralRow[];
    },
  });
}

/**
 * The two counters above the table.
 *
 * Both count the same rows — a referral is "successful" exactly when it has
 * earned its month — so they are derived together rather than from two
 * predicates that could drift. A month is earned once the referred owner's
 * contract is recorded, which is `contract_signed`, and stays counted once it
 * has been `credited`.
 */
export function referralTotals(rows: readonly OwnerReferralRow[]): {
  successful: number;
  feeFreeMonths: number;
} {
  const earned = rows.filter(
    (r) => r.status === "contract_signed" || r.status === "credited"
  ).length;
  return { successful: earned, feeFreeMonths: earned };
}

/** Why a redeem was refused, in the words the page shows. */
export type RedeemError =
  | "code_invalid"
  | "own_code"
  | "already_redeemed"
  | "unknown";

function classifyRedeemError(message: string): RedeemError {
  if (message.includes("own_code")) return "own_code";
  if (message.includes("already_redeemed")) return "already_redeemed";
  if (message.includes("code_invalid")) return "code_invalid";
  return "unknown";
}

export function useRedeemOwnerReferralCode() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation<string, RedeemError, string>({
    mutationFn: async (code: string): Promise<string> => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (
            fn: string,
            args: Record<string, unknown>
          ) => Promise<{ data: unknown; error: { message?: string } | null }>;
        }
      ).rpc("redeem_owner_referral_code", { _code: code });
      // The RPC RAISEs a bare reason; it is turned into one of the page's own
      // messages here rather than showing a Postgres error to an owner.
      if (error) throw classifyRedeemError(error.message ?? "");
      return String(data ?? code);
    },
    onSuccess: () => {
      // The claim card reads from my_owner_referral, which has just changed.
      void qc.invalidateQueries({ queryKey: ["owner-referral"] });
      void qc.invalidateQueries({ queryKey: myOwnerReferralsKey(user?.id) });
    },
  });
}

/** An invite rejection the card words differently from a generic failure. */
export type InviteError = Error & { reason?: "rate_limited" };

export function useSendReferralInvite() {
  return useMutation({
    mutationFn: async (email: string): Promise<void> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not signed in");

      const res = await fetch(
        // See useDownloadPayoutStatement: the raw env var can be undefined in a
        // supported configuration; supabaseUrl is the resolved one.
        `${supabaseUrl}/functions/v1/owner-referral-invite`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          // Only the recipient. The code is read server-side from the caller's
          // own row, so an invitation can never carry somebody else's.
          body: JSON.stringify({ email }),
        }
      );
      if (!res.ok) {
        // 429 is the per-owner send cap (claim_owner_referral_invite). It is a
        // normal thing for an owner to hit, not a failure to report as one, so
        // the reason is carried through for the card to word it properly.
        const err = new Error(`Invite failed (${res.status})`) as InviteError;
        if (res.status === 429) err.reason = "rate_limited";
        throw err;
      }
    },
  });
}
